/**
 * Stage 1A — visible impact review before a size / case / base change is saved.
 * Counts come from the read-only POST /beverage-products/{id}/impact-review.
 */
import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const FIELD_LABELS = { size: 'Size', base: 'Base unit', case_qty: 'Items per case', removed: 'Removed unit', case_size: 'Case / outer size' };

function ChangeLine({ c }) {
  const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
  return (
    <li className="text-xs text-slate-700">
      <span className="font-medium">{c.unit ? `${c.unit} · ` : ''}{FIELD_LABELS[c.field] || c.field}</span>
      {c.field !== 'removed' && <>: {fmt(c.old)} → {fmt(c.new)}</>}
    </li>
  );
}

function CountRow({ label, value, testid, muted }) {
  return (
    <div className="flex items-center justify-between text-xs" data-testid={testid}>
      <span className={muted ? 'text-slate-500' : 'text-slate-700'}>{label}</span>
      <span className="font-mono font-semibold tabular-nums">{value}</span>
    </div>
  );
}

export function ImpactReviewDialog({ review, open, onCancel, onConfirm, saving }) {
  if (!review) return null;
  const c = review.counts || {};
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent className="max-w-lg" data-testid="impact-review-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Review the impact of this change</AlertDialogTitle>
          <AlertDialogDescription>Nothing has been saved yet. Confirm to save exactly as before, or cancel to keep the product unchanged.</AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2" data-testid="impact-review-changes">
          {(review.changes || []).map((ch, i) => <ChangeLine key={i} c={ch} />)}
        </ul>
        <div className="space-y-1.5 rounded border border-slate-200 px-3 py-2">
          <CountRow label="Site price rows for this product" value={c.price_rows ?? 0} testid="impact-price-rows" />
          <CountRow label="…of which will be re-pointed to the new size" value={c.price_rows_at_changed_size ?? 0} testid="impact-price-rows-changed" />
          <CountRow label="Unlocked stock audits that will be recalculated" value={c.unlocked_audits_recalculated ?? 0} testid="impact-unlocked-audits" />
          <CountRow label="Site measure overrides" value={c.site_measure_overrides ?? 0} testid="impact-site-measures" />
          <CountRow label="Site case overrides" value={c.site_case_overrides ?? 0} testid="impact-site-case-overrides" />
          <CountRow label="PSB crosswalks to this product" value={c.crosswalks ?? 0} testid="impact-crosswalks" />
          <CountRow label="Locked / issued audits — not affected" value={c.locked_audits_not_affected ?? 0} testid="impact-locked-audits" muted />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} data-testid="impact-review-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={saving} data-testid="impact-review-confirm">
            {saving ? 'Saving…' : 'Confirm and save'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
